if (!customElements.get('tmf-proposal-booking')) {
  customElements.define(
    'tmf-proposal-booking',
    class TmfProposalBooking extends HTMLElement {
      connectedCallback() {
        if (this._connected) return;
        this._connected = true;
        this.abortController = new AbortController();
        const signal = this.abortController.signal;
        this.form = this.querySelector('form');
        this.proposalButton = this.form?.querySelector('button[type="submit"]');
        this.proposalButtonAriaLabel = this.proposalButton?.getAttribute('aria-label');
        this.proposalOpeningLabel = this.dataset.proposalOpeningLabel || this.proposalButtonAriaLabel || '';
        this.inline = this.hasAttribute('data-inline');
        const dialogId = String(this.dataset.dialogId || '').trim();
        this.dialog = dialogId ? this.querySelector(`#${CSS.escape(dialogId)}`) : null;
        this.bookingPanel = this.querySelector('[data-booking-panel]');
        this.closeButtons = Array.from(this.querySelectorAll('[data-booking-close]'));
        this.monthLabel = this.querySelector('[data-booking-month]');
        this.daysNode = this.querySelector('[data-booking-days]');
        this.slotsNode = this.querySelector('[data-booking-slots]');
        this.selectedDateNode = this.querySelector('[data-booking-selected-date]');
        this.emptyDateLabel = this.selectedDateNode?.textContent.trim() || '';
        this.emptySlotLabel = this.querySelector('[data-booking-empty-label]')?.textContent.trim() || '';
        this.statusNode = this.querySelector('[data-booking-status]');
        this.confirmButton = this.querySelector('[data-booking-confirm]');
        this.timezoneNode = this.querySelector('[data-booking-timezone]');
        this.weekdayNodes = Array.from(this.querySelectorAll('[data-booking-weekdays] > span'));
        this.previousButton = this.querySelector('[data-booking-prev]');
        this.nextButton = this.querySelector('[data-booking-next]');
        this.headerNode = this.querySelector('.tmf-proposal-booking__header');
        this.schedulerNode = this.querySelector('[data-booking-scheduler]');
        this.footerNode = this.querySelector('[data-booking-footer]');
        this.successNode = this.querySelector('[data-booking-success]');
        this.successTitleNode = this.querySelector('[data-booking-success-title]');
        this.successCopyNode = this.querySelector('[data-booking-success-copy]');
        this.meetLinkNode = this.querySelector('[data-booking-meet-link]');
        this.methodChoices = Array.from(this.querySelectorAll('input[name="booking_method"]'));
        this.methodFieldset = this.querySelector('[data-booking-method]');
        this.methodLabelNode = this.querySelector('[data-booking-method-label]');
        this.durationLabelNode = this.querySelector('[data-booking-duration], [data-booking-duration-label]');
        this.phoneField = this.querySelector('[data-booking-phone-field]');
        this.phoneInput = this.querySelector('input[name="booking_phone"]');
        this.apiBase = (this.dataset.apiBase || '/apps/todays-studio').replace(/\/$/, '');
        this.bookingSurface = this.safeBookingSurface(this.dataset.bookingSurface);
        this.defaultMethod = this.safeBookingMethod(this.dataset.bookingMethod || this.dataset.defaultMethod || 'google_meet');
        this.fixedMethod = this.inline || this.dataset.methodLocked === 'true';
        if (this.fixedMethod) this.removeAttribute('data-booking-method');
        this.slots = [];
        this.visibleMonth = new Date();
        this.selectedDate = '';
        this.selectedSlot = null;
        this.demoMode = false;
        this.timeZone = 'Europe/Paris';
        this.locale = document.documentElement.lang || 'fr-FR';
        this.bookingI18n = this.readBookingI18n();
        this.methodControlsLocked = false;
        this.localPreview = ['127.0.0.1', 'localhost', '::1'].includes(window.location.hostname);

        this.form?.addEventListener('submit', this.onSubmit, { signal });
        this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close(), { signal }));
        this.confirmButton?.addEventListener('click', this.confirm, { signal });
        this.previousButton?.addEventListener('click', () => this.shiftMonth(-1), { signal });
        this.nextButton?.addEventListener('click', () => this.shiftMonth(1), { signal });
        this.methodChoices.forEach((choice) => choice.addEventListener('change', this.onMethodChange, { signal }));
        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        }, { signal });
        this.dialog?.addEventListener('close', () => {
          document.documentElement.classList.remove('tmf-proposal-booking-open');
          this.resetBookingMethod();
        }, { signal });
        this.renderWeekdays();
        this.resetBookingMethod();
        if (this.inline) this.startBooking({ focusTitle: false });
      }

      disconnectedCallback() {
        this.abortController?.abort();
        document.documentElement.classList.remove('tmf-proposal-booking-open');
        this._connected = false;
      }

      onSubmit = async (event) => {
        event.preventDefault();
        if (!this.form?.reportValidity()) return;
        if (this.proposalOpening) return;
        this.setProposalOpening(true);
        try {
          await this.nextAnimationFrame();
          await this.nextAnimationFrame();
          this.open();
        } finally {
          this.setProposalOpening(false);
        }
      };

      nextAnimationFrame() {
        return new Promise((resolve) => window.requestAnimationFrame(resolve));
      }

      setProposalOpening(opening) {
        this.proposalOpening = Boolean(opening);
        if (!this.proposalButton) return;
        this.proposalButton.disabled = this.proposalOpening;
        if (this.proposalOpening) {
          this.proposalButton.setAttribute('aria-busy', 'true');
          this.proposalButton.setAttribute('aria-label', this.proposalOpeningLabel);
          return;
        }
        this.proposalButton.removeAttribute('aria-busy');
        if (this.proposalButtonAriaLabel === null) this.proposalButton.removeAttribute('aria-label');
        else this.proposalButton.setAttribute('aria-label', this.proposalButtonAriaLabel);
      }

      onMethodChange = () => {
        this.syncMethodFields();
        if (this.bookingStarted) this.loadSlots();
      };

      contact(method = this.bookingMethod()) {
        const data = new FormData(this.form);
        const contact = {
          name: String(data.get('booking_name') || '').trim(),
          email: String(data.get('booking_email') || '').trim(),
          company: String(data.get('booking_company') || '').trim()
        };
        if (method === 'phone') {
          contact.phone = String(data.get('booking_phone') || '').trim();
        }
        return contact;
      }

      bookingIntention() {
        const method = this.bookingMethod();
        return {
          method,
          contact: this.contact(method),
          slot: this.selectedSlot ? { ...this.selectedSlot } : null,
          surface: this.bookingSurface,
          timeZone: this.timeZone
        };
      }

      bookingMethod() {
        const selected = this.methodChoices.find((choice) => choice.checked)?.value;
        return this.safeBookingMethod(selected || this.defaultMethod);
      }

      safeBookingMethod(value) {
        return ['google_meet', 'phone', 'showroom'].includes(value) ? value : 'google_meet';
      }

      safeBookingSurface(value) {
        return value === 'homepage' ? 'homepage' : '';
      }

      readBookingI18n() {
        const source = this.querySelector('[data-booking-i18n]')?.textContent || '';
        if (!source.trim()) return {};
        try {
          const translations = JSON.parse(source);
          return translations && typeof translations === 'object' && !Array.isArray(translations)
            ? translations
            : {};
        } catch {
          return {};
        }
      }

      bookingText(path, frenchFallback, englishFallback = '') {
        const translated = String(
          String(path || '')
            .split('.')
            .filter(Boolean)
            .reduce((value, key) => value?.[key], this.bookingI18n) || '',
        ).trim();
        if (translated && !/^translation missing:/i.test(translated)) return translated;
        if (/^en(?:-|$)/i.test(this.locale)) return englishFallback || frenchFallback;
        return frenchFallback;
      }

      safeMeetLink(value) {
        try {
          const url = new URL(String(value || ''));
          if (url.protocol !== 'https:' || url.hostname !== 'meet.google.com' || url.username || url.password) return '';
          return `${url.origin}${url.pathname}`;
        } catch {
          return '';
        }
      }

      notificationConfirmationCopy(payload = {}) {
        if (payload?.clientEmailAccepted === false) {
          return this.bookingText(
            'notification.emailUnconfirmed',
            'Votre rendez-vous est enregistré, mais l’envoi de l’e-mail de confirmation n’est pas encore confirmé. Pour toute question, appelez-nous au 01 85 13 44 26.',
            'Your appointment is booked, but the confirmation email has not been confirmed as sent yet. For any questions, call us on +33 1 85 13 44 26.',
          );
        }
        return this.bookingText(
          'notification.confirmed',
          'Vous allez recevoir l’invitation calendrier et l’e-mail de confirmation.',
          'You’ll receive a calendar invitation and confirmation email.',
        );
      }

      methodPresentation(method = this.bookingMethod()) {
        const presentations = {
          google_meet: {
            duration: '30 min',
            channel: this.bookingText('methods.googleMeet.channel', 'Google Meet', 'Google Meet'),
            confirm: this.bookingText('methods.googleMeet.confirm', 'Réserver', 'Book'),
            loading: this.bookingText('methods.googleMeet.loading', 'Réservation en cours…', 'Confirming your booking…'),
            guidance: this.bookingText(
              'methods.googleMeet.guidance',
              'Choisissez un créneau. L’invitation sera envoyée par e-mail.',
              'Choose a time slot. We’ll send the calendar invitation by email.',
            ),
            demo: this.bookingText(
              'methods.googleMeet.demo',
              'Aperçu local : aucun rendez-vous ni e-mail ne sera créé.',
              'Local preview: no appointment or email will be created.',
            ),
            successTitle: this.bookingText(
              'methods.googleMeet.successTitle',
              'Votre échange est réservé.',
              'Your project call is booked.',
            ),
            successCopy: (slot, payload = {}) => payload?.clientEmailAccepted === false
              ? this.interpolateBookingMessage(
                this.bookingText(
                  'methods.googleMeet.successCopyEmailFailed',
                  'Votre échange est réservé pour [[date]] à [[time]]. [[notification]]',
                  'Your project call is booked for [[date]] at [[time]]. [[notification]]',
                ),
                slot,
                { notification: this.notificationConfirmationCopy(payload) },
              )
              : this.interpolateBookingMessage(
                this.bookingText(
                  'methods.googleMeet.successCopy',
                  'Vous allez recevoir l’invitation calendrier et l’e-mail de confirmation pour [[date]] à [[time]].',
                  'You’ll receive the calendar invitation and confirmation email for [[date]] at [[time]].',
                ),
                slot,
              ),
            demoTitle: this.bookingText('methods.googleMeet.demoTitle', 'Parcours prêt en local.', 'Booking flow ready locally.'),
            demoCopy: this.bookingText(
              'methods.googleMeet.demoCopy',
              'Aucun rendez-vous ni e-mail n’a été créé.',
              'No appointment or email has been created.',
            ),
          },
          phone: {
            duration: this.bookingSurface === 'homepage' ? '30 min' : '15 min',
            channel: this.bookingText('methods.phone.channel', 'Appel téléphonique', 'Phone call'),
            confirm: this.bookingText('methods.phone.confirm', 'Réserver', 'Book'),
            loading: this.bookingText('methods.phone.loading', 'Réservation en cours…', 'Confirming your booking…'),
            guidance: this.bookingText(
              'methods.phone.guidance',
              'Choisissez un créneau pour planifier votre rappel.',
              'Choose a time slot to schedule your call-back.',
            ),
            demo: this.bookingText(
              'methods.phone.demo',
              'Aperçu local : aucun rappel ni e-mail ne sera créé.',
              'Local preview: no call-back or email will be created.',
            ),
            successTitle: this.bookingText('methods.phone.successTitle', 'Votre rappel est réservé.', 'Your call-back is booked.'),
            successCopy: (slot, payload = {}) => `${this.interpolateBookingMessage(
              this.bookingText(
                'methods.phone.successCopy',
                'Nous vous appellerons le [[date]] à [[time]].',
                'We’ll call you on [[date]] at [[time]].',
              ),
              slot,
            )} ${this.notificationConfirmationCopy(payload)}`,
            demoTitle: this.bookingText(
              'methods.phone.demoTitle',
              'Parcours de rappel prêt en local.',
              'Call-back flow ready locally.',
            ),
            demoCopy: this.bookingText(
              'methods.phone.demoCopy',
              'Aucun rappel ni e-mail n’a été créé.',
              'No call-back or email has been created.',
            ),
          },
          showroom: {
            duration: '30 min',
            channel: this.bookingText('methods.showroom.channel', 'Showroom Paris', 'Paris showroom'),
            confirm: this.bookingText('methods.showroom.confirm', 'Réserver la visite', 'Book the visit'),
            loading: this.bookingText('methods.showroom.loading', 'Validation de la visite…', 'Confirming your showroom visit…'),
            guidance: this.bookingText(
              'methods.showroom.guidance',
              'Choisissez un créneau pour visiter le showroom.',
              'Choose a time slot to visit the showroom.',
            ),
            demo: this.bookingText(
              'methods.showroom.demo',
              'Aperçu local : aucune visite ni e-mail ne sera créé.',
              'Local preview: no visit or email will be created.',
            ),
            successTitle: this.bookingText(
              'methods.showroom.successTitle',
              'Votre visite est réservée.',
              'Your showroom visit is booked.',
            ),
            successCopy: (slot, payload = {}) => `${this.interpolateBookingMessage(
              this.bookingText(
                'methods.showroom.successCopy',
                'Nous vous attendons au 79 rue du Temple, 75003 Paris, le [[date]] à [[time]].',
                'We’ll welcome you at 79 rue du Temple, 75003 Paris on [[date]] at [[time]].',
              ),
              slot,
            )} ${this.notificationConfirmationCopy(payload)}`,
            demoTitle: this.bookingText(
              'methods.showroom.demoTitle',
              'Parcours showroom prêt en local.',
              'Showroom flow ready locally.',
            ),
            demoCopy: this.bookingText(
              'methods.showroom.demoCopy',
              'Aucune visite, e-mail ni invitation Agenda n’a été créé.',
              'No visit, email or calendar invitation has been created.',
            ),
          },
        };
        const details = presentations[this.safeBookingMethod(method)];
        if (!this.fixedMethod || this.safeBookingMethod(method) !== this.defaultMethod) return details;

        const override = (value) => {
          const normalized = String(value || '').trim();
          if (!normalized || /^translation missing:/i.test(normalized)) return '';
          return normalized;
        };
        const nodeText = (node) => override(node?.textContent || '');
        const durationOverride = override(this.dataset.bookingDuration);

        return {
          ...details,
          duration: durationOverride ? `${durationOverride} min` : (nodeText(this.durationLabelNode) || details.duration),
          channel: override(this.dataset.bookingChannel) || nodeText(this.methodLabelNode) || details.channel,
          confirm: override(this.dataset.bookingConfirmLabel) || nodeText(this.confirmButton) || details.confirm,
          loading: override(this.dataset.bookingLoadingLabel) || details.loading,
          guidance: override(this.dataset.bookingGuidanceLabel) || details.guidance,
          demo: override(this.dataset.bookingDemoLabel) || details.demo,
          successTitle: override(this.dataset.bookingSuccessTitle) || details.successTitle,
          successCopy: (slot, payload = {}) => override(this.dataset.bookingSuccessCopy)
            ? `${this.interpolateBookingMessage(override(this.dataset.bookingSuccessCopy), slot)} ${this.notificationConfirmationCopy(payload)}`
            : details.successCopy(slot, payload),
          demoTitle: override(this.dataset.bookingDemoTitle) || details.demoTitle,
          demoCopy: override(this.dataset.bookingDemoCopy) || details.demoCopy,
        };
      }

      interpolateBookingMessage(message, slot = {}, replacements = {}) {
        let interpolated = String(message || '')
          .replaceAll('[[date]]', String(slot.dateLabel || ''))
          .replaceAll('[[time]]', String(slot.timeLabel || ''))
          .replaceAll('[[address]]', String(this.dataset.bookingAddress || ''));
        Object.entries(replacements).forEach(([key, value]) => {
          interpolated = interpolated.replaceAll(`[[${key}]]`, String(value || ''));
        });
        return interpolated;
      }

      syncMethodFields = () => {
        const method = this.bookingMethod();
        const details = this.methodPresentation(method);
        const isPhone = method === 'phone';
        this.methodChoices.forEach((choice) => {
          choice.disabled = this.methodControlsLocked || this.fixedMethod;
          choice.closest('label')?.classList.toggle('is-selected', choice.checked);
        });
        if (this.methodFieldset) this.methodFieldset.hidden = this.fixedMethod;
        if (this.phoneField) this.phoneField.hidden = !isPhone;
        if (this.phoneInput) {
          const modalClosed = Boolean(this.dialog && !this.dialog.open);
          this.phoneInput.required = isPhone;
          this.phoneInput.disabled = this.methodControlsLocked || !isPhone || modalClosed;
          this.phoneInput.setAttribute('aria-required', String(isPhone));
        }
        if (this.methodLabelNode) this.methodLabelNode.textContent = details.channel;
        if (this.durationLabelNode) this.durationLabelNode.textContent = details.duration;
        if (this.confirmButton) this.confirmButton.textContent = details.confirm;
        if (this.slots.length && !this.selectedSlot && !this.statusNode?.classList.contains('is-error')) {
          this.setMethodGuidance();
        }
      };

      setMethodControlsLocked(locked) {
        this.methodControlsLocked = Boolean(locked);
        this.syncMethodFields();
      }

      resetBookingMethod() {
        this.methodControlsLocked = false;
        this.methodChoices.forEach((choice) => {
          choice.checked = choice.value === this.defaultMethod;
        });
        if (this.phoneInput) this.phoneInput.value = '';
        this.syncMethodFields();
      }

      setMethodGuidance() {
        const details = this.methodPresentation();
        if (this.demoMode) {
          this.setStatus(details.demo);
          return;
        }
        this.setStatus(details.guidance);
      }

      validateBookingMethod() {
        this.syncMethodFields();
        if (this.bookingMethod() !== 'phone') return true;
        return Boolean(this.phoneInput?.reportValidity());
      }

      open() {
        if (!this.dialog) return;
        this.startBooking({ showDialog: true, focusTitle: true });
      }

      startBooking({ showDialog = false, focusTitle = false } = {}) {
        this.resetCompletion();
        this.renderDays();
        this.renderSlots();
        if (showDialog && this.dialog) {
          if (!this.dialog.open && typeof this.dialog.showModal === 'function') this.dialog.showModal();
          else if (!this.dialog.open) this.dialog.setAttribute('open', '');
          document.documentElement.classList.add('tmf-proposal-booking-open');
          this.syncMethodFields();
        }
        this.bookingStarted = true;
        if (focusTitle) this.bookingPanel?.querySelector('.tmf-proposal-booking__title')?.focus({ preventScroll: true });
        return this.loadSlots();
      }

      close() {
        if (!this.dialog) return;
        if (typeof this.dialog.close === 'function') this.dialog.close();
        else {
          this.dialog.removeAttribute('open');
          this.resetBookingMethod();
        }
        this.bookingStarted = false;
        document.documentElement.classList.remove('tmf-proposal-booking-open');
      }

      async loadSlots() {
        this.setStatus(this.dataset.messageLoadingSlots || this.bookingText(
          'messages.loadingSlots',
          'Chargement des créneaux disponibles…',
          'Loading available times…',
        ));
        this.confirmButton.disabled = true;
        this.selectedSlot = null;
        try {
          if (this.localPreview) {
            this.demoMode = true;
            this.slots = this.demoSlots();
          } else {
            const pageOrigin = /^https?:\/\//.test(window.location.origin)
              ? window.location.origin
              : 'https://todaymerch.local';
            const availabilityUrl = new URL(`${this.apiBase}/forms/proposal-booking/availability`, pageOrigin);
            availabilityUrl.searchParams.set('method', this.bookingMethod());
            if (this.bookingSurface) availabilityUrl.searchParams.set('surface', this.bookingSurface);
            const payload = await this.fetchJson(availabilityUrl.pathname + availabilityUrl.search, {
              method: 'GET',
              credentials: 'include',
              headers: { Accept: 'application/json' }
            });
            this.demoMode = Boolean(payload.demo);
            this.timeZone = String(payload.timeZone || 'Europe/Paris');
            const durationMinutes = Number(payload.durationMinutes);
            if (this.durationLabelNode && Number.isInteger(durationMinutes) && durationMinutes > 0) {
              this.durationLabelNode.textContent = `${durationMinutes} min`;
            }
            this.slots = (Array.isArray(payload.slots) ? payload.slots : [])
              .map((slot) => this.normalizeSlot(slot))
              .filter(Boolean);
          }
          if (this.timezoneNode) this.timezoneNode.textContent = this.timeZone;
        } catch (error) {
          this.demoMode = false;
          this.slots = [];
          this.selectedDate = '';
          this.render();
          this.setStatus(this.publicErrorMessage(error), true);
          return;
        }

        const firstSlot = this.slots[0];
        if (firstSlot) {
          const [year, month] = firstSlot.date.split('-').map(Number);
          this.visibleMonth = new Date(year, month - 1, 1);
          this.selectedDate = firstSlot.date;
        } else {
          this.selectedDate = '';
        }
        this.render();
        if (!this.slots.length) {
          this.setStatus(this.dataset.messageNoSlots || this.bookingText(
            'messages.noSlots',
            'Aucun créneau n’est disponible pour le moment.',
            'No time slots are available right now.',
          ), true);
        } else {
          this.setMethodGuidance();
        }
      }

      renderWeekdays() {
        if (this.weekdayNodes.length !== 7) return;
        const monday = new Date(Date.UTC(2026, 7, 3, 12));
        const formatter = new Intl.DateTimeFormat(this.locale, { weekday: 'short', timeZone: 'UTC' });
        this.weekdayNodes.forEach((node, index) => {
          const day = new Date(monday);
          day.setUTCDate(monday.getUTCDate() + index);
          node.textContent = formatter.format(day).replace('.', '').toLocaleUpperCase(this.locale);
        });
      }

      demoSlots() {
        const slots = [];
        const now = new Date();
        const durationMinutes = this.methodPresentation().duration === '15 min' ? 15 : 30;
        for (let offset = 1; offset <= 45; offset += 1) {
          const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset, 12, 0, 0, 0);
          if ([0, 6].includes(day.getDay())) continue;
          [[9, 30], [10, 0], [10, 30], [11, 0], [11, 30], [14, 0], [14, 30], [15, 0], [15, 30], [16, 0], [16, 30], [17, 0]].forEach(([hour, minute]) => {
            const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, minute, 0, 0);
            const end = new Date(start);
            end.setMinutes(start.getMinutes() + durationMinutes);
            slots.push({
              id: start.toISOString(),
              start: start.toISOString(),
              end: end.toISOString(),
              date: this.dateKey(start),
              dateLabel: new Intl.DateTimeFormat(this.locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(start),
              timeLabel: new Intl.DateTimeFormat(this.locale, { hour: '2-digit', minute: '2-digit' }).format(start)
            });
          });
        }
        return slots.slice(0, 240);
      }

      normalizeSlot(slot) {
        if (!slot || typeof slot !== 'object') return null;
        const start = String(slot.start || '');
        const end = String(slot.end || '');
        const date = String(slot.date || '');
        if (!Number.isFinite(new Date(start).getTime()) || !Number.isFinite(new Date(end).getTime()) || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
        return {
          id: String(slot.id || start),
          start,
          end,
          date,
          dateLabel: new Intl.DateTimeFormat(this.locale, {
            timeZone: this.timeZone,
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          }).format(new Date(start)),
          timeLabel: new Intl.DateTimeFormat(this.locale, {
            timeZone: this.timeZone,
            hour: '2-digit',
            minute: '2-digit',
          }).format(new Date(start))
        };
      }

      dateKey(dateOrYear, month, day) {
        const date = dateOrYear instanceof Date ? dateOrYear : new Date(dateOrYear, month, day);
        const year = date.getFullYear();
        const monthNumber = String(date.getMonth() + 1).padStart(2, '0');
        const dayNumber = String(date.getDate()).padStart(2, '0');
        return `${year}-${monthNumber}-${dayNumber}`;
      }

      async fetchJson(url, options = {}) {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), options.method === 'POST' ? 30000 : 12000);
        try {
          const response = await fetch(url, { ...options, signal: controller.signal });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) {
            const error = new Error(String(payload.error || 'request failed'));
            error.code = String(payload.code || 'PROPOSAL_BOOKING_REQUEST_FAILED');
            throw error;
          }
          return payload;
        } catch (error) {
          if (error?.name === 'AbortError') {
            const timeoutError = new Error('request timeout');
            timeoutError.code = 'PROPOSAL_BOOKING_REQUEST_FAILED';
            throw timeoutError;
          }
          throw error;
        } finally {
          window.clearTimeout(timeout);
        }
      }

      shiftMonth(delta) {
        this.visibleMonth = new Date(this.visibleMonth.getFullYear(), this.visibleMonth.getMonth() + delta, 1);
        this.render();
      }

      slotsByDate() {
        return this.slots.reduce((map, slot) => {
          if (!map.has(slot.date)) map.set(slot.date, []);
          map.get(slot.date).push(slot);
          return map;
        }, new Map());
      }

      render() {
        this.renderDays();
        this.renderSlots();
      }

      renderDays() {
        if (!this.daysNode || !this.monthLabel) return;
        const available = this.slotsByDate();
        const year = this.visibleMonth.getFullYear();
        const month = this.visibleMonth.getMonth();
        this.monthLabel.textContent = new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric' }).format(this.visibleMonth);
        this.daysNode.replaceChildren();
        const first = new Date(year, month, 1);
        const leading = (first.getDay() + 6) % 7;
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const todayKey = this.dateKey(new Date());

        for (let index = 0; index < leading + daysInMonth; index += 1) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'tmf-proposal-booking__day';
          if (index < leading) {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
            this.daysNode.append(button);
            continue;
          }
          const day = index - leading + 1;
          const date = new Date(year, month, day);
          const key = this.dateKey(date);
          const enabled = available.has(key);
          button.textContent = String(day).padStart(2, '0');
          button.setAttribute('aria-disabled', String(!enabled));
          button.setAttribute('aria-pressed', String(key === this.selectedDate));
          button.disabled = !enabled;
          button.classList.toggle('is-selected', key === this.selectedDate);
          button.classList.toggle('is-today', key === todayKey);
          button.addEventListener('click', () => {
            this.selectedDate = key;
            this.selectedSlot = null;
            this.render();
            this.setMethodGuidance();
          });
          this.daysNode.append(button);
        }
      }

      renderSlots() {
        if (!this.slotsNode || !this.selectedDateNode || !this.confirmButton) return;
        const slots = this.slotsByDate().get(this.selectedDate) || [];
        this.slotsNode.replaceChildren();
        this.confirmButton.disabled = !this.selectedSlot;
        if (!slots.length) {
          this.selectedDateNode.textContent = this.emptyDateLabel;
          const empty = document.createElement('p');
          empty.className = 'tmf-proposal-booking__empty';
          empty.textContent = this.emptySlotLabel;
          this.slotsNode.append(empty);
          return;
        }
        this.selectedDateNode.textContent = slots[0].dateLabel.replace(/^./, (letter) => letter.toUpperCase());
        slots.forEach((slot) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'tmf-proposal-booking__slot';
          button.textContent = slot.timeLabel;
          button.classList.toggle('is-selected', this.selectedSlot?.start === slot.start);
          button.setAttribute('aria-pressed', String(this.selectedSlot?.start === slot.start));
          button.addEventListener('click', () => {
            this.selectedSlot = slot;
            this.renderSlots();
            this.setStatus(this.interpolateBookingMessage(
              this.dataset.messageSlotSelected || this.bookingText(
                'messages.slotSelected',
                '[[date]] à [[time]] sélectionné.',
                '[[date]] at [[time]] selected.',
              ),
              slot,
            ));
          });
          this.slotsNode.append(button);
        });
      }

      confirm = async () => {
        if (this.submissionInFlight || !this.selectedSlot || !this.confirmButton) return;
        if (!this.form?.reportValidity()) return;
        if (!this.validateBookingMethod()) return;
        const intention = this.bookingIntention();
        const details = this.methodPresentation(intention.method);
        const website = String(new FormData(this.form).get('website') || '');
        this.submissionInFlight = true;
        this.setMethodControlsLocked(true);
        this.confirmButton.disabled = true;
        this.confirmButton.setAttribute('aria-busy', 'true');
        this.confirmButton.setAttribute('aria-label', details.loading);
        this.setStatus('');
        try {
          if (this.demoMode) {
            this.showCompletion({}, true, intention);
            return;
          }
          const requestBody = {
            method: intention.method,
            ...(intention.surface ? { surface: intention.surface } : {}),
            contact: intention.contact,
            start: intention.slot.start,
            time_zone: intention.timeZone,
            page_url: `${window.location.origin}${window.location.pathname}`,
            locale: this.locale,
            website
          };
          const signature = JSON.stringify(requestBody);
          if (this.lastPayloadSignature && this.lastPayloadSignature !== signature) {
            delete this.form.dataset.autoreplyRequestId;
          }
          this.lastPayloadSignature = signature;
          if (!this.form.dataset.autoreplyRequestId) {
            this.form.dataset.autoreplyRequestId = window.crypto?.randomUUID
              ? `TM-${window.crypto.randomUUID()}`
              : `TM-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
          }
          const payload = await this.fetchJson(`${this.apiBase}/forms/proposal-booking`, {
            method: 'POST',
            credentials: 'include',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...requestBody, request_id: this.form.dataset.autoreplyRequestId })
          });
          if (payload?.ok !== true || typeof payload?.eventId !== 'string' || !payload.eventId.trim()) {
            const unconfirmed = new Error('Booking was not confirmed');
            unconfirmed.code = 'PROPOSAL_BOOKING_REQUEST_FAILED';
            throw unconfirmed;
          }
          delete this.form.dataset.autoreplyRequestId;
          this.showCompletion(payload, false, intention);
        } catch (error) {
          const freshIntentionCodes = [
            'PROPOSAL_BOOKING_SLOT_TAKEN',
            'PROPOSAL_BOOKING_SLOT_INVALID',
            'PROPOSAL_BOOKING_REQUEST_ID_CONFLICT',
            'AUTOREPLY_REQUEST_ID_CONFLICT'
          ];
          if (freshIntentionCodes.includes(String(error?.code || ''))) {
            delete this.form.dataset.autoreplyRequestId;
          }
          if (['PROPOSAL_BOOKING_SLOT_TAKEN', 'PROPOSAL_BOOKING_SLOT_INVALID'].includes(String(error?.code || ''))) {
            this.selectedSlot = null;
            this.renderSlots();
          } else {
            this.confirmButton.disabled = false;
          }
          this.setStatus(this.publicErrorMessage(error), true);
          this.setMethodControlsLocked(false);
        } finally {
          this.submissionInFlight = false;
          this.confirmButton.removeAttribute('aria-busy');
          this.confirmButton.removeAttribute('aria-label');
        }
      };

      resetCompletion() {
        this.setMethodControlsLocked(false);
        if (this.headerNode) this.headerNode.hidden = false;
        if (this.schedulerNode) this.schedulerNode.hidden = false;
        if (this.footerNode) this.footerNode.hidden = false;
        if (this.successNode) this.successNode.hidden = true;
        if (this.meetLinkNode) {
          this.meetLinkNode.hidden = true;
          this.meetLinkNode.removeAttribute('href');
        }
        if (this.successCopyNode) this.successCopyNode.textContent = '';
      }

      showCompletion(payload = {}, demo = false, intention = this.bookingIntention()) {
        const details = this.methodPresentation(intention.method);
        const slot = intention.slot || this.selectedSlot;
        if (this.headerNode) this.headerNode.hidden = true;
        if (this.schedulerNode) this.schedulerNode.hidden = true;
        if (this.footerNode) this.footerNode.hidden = true;
        if (this.successNode) this.successNode.hidden = false;
        if (this.successTitleNode) {
          this.successTitleNode.textContent = demo ? details.demoTitle : details.successTitle;
        }
        if (this.successCopyNode) {
          this.successCopyNode.textContent = demo ? details.demoCopy : details.successCopy(slot, payload);
        }
        if (this.meetLinkNode) {
          this.meetLinkNode.hidden = true;
          this.meetLinkNode.removeAttribute('href');
        }

        if (!demo && payload.ok === true && typeof payload.eventId === 'string' && /^[0-9a-v]{5,180}$/.test(payload.eventId)) {
          document.dispatchEvent(new CustomEvent('todaymerch:booking-complete', {
            detail: {
              eventId: payload.eventId,
              method: intention.method,
              surface: intention.surface || this.bookingSurface || ''
            }
          }));
        }

        this.successTitleNode?.focus?.({ preventScroll: true });
      }

      publicErrorMessage(error) {
        if (this.fixedMethod && this.dataset.messageGenericError) {
          const localizedMessages = {
            PROPOSAL_BOOKING_SLOT_TAKEN: this.dataset.messageSlotTaken,
            PROPOSAL_BOOKING_SLOT_INVALID: this.dataset.messageSlotTaken,
            PROPOSAL_BOOKING_PHONE_INVALID: this.dataset.messagePhoneInvalid,
            PROPOSAL_BOOKING_RATE_LIMITED: this.dataset.messageRateLimited,
            AUTOREPLY_RATE_LIMITED: this.dataset.messageRateLimited,
            AUTOREPLY_CAPTCHA_REQUIRED: this.dataset.messageCaptchaError,
            AUTOREPLY_CAPTCHA_REJECTED: this.dataset.messageCaptchaError,
            AUTOREPLY_CAPTCHA_TIMEOUT: this.dataset.messageCaptchaError,
            AUTOREPLY_CAPTCHA_UNAVAILABLE: this.dataset.messageCaptchaError,
          };
          return localizedMessages[String(error?.code || '')] || this.dataset.messageGenericError;
        }
        const messages = {
          PROPOSAL_BOOKING_SLOT_TAKEN: this.bookingText(
            'errors.slotTaken',
            'Ce créneau vient d’être réservé. Choisissez-en un autre.',
            'This time slot has just been booked. Please choose another one.',
          ),
          PROPOSAL_BOOKING_SLOT_INVALID: this.bookingText(
            'errors.slotInvalid',
            'Ce créneau n’est plus disponible. Choisissez-en un autre.',
            'This time slot is no longer available. Please choose another one.',
          ),
          PROPOSAL_BOOKING_PHONE_INVALID: this.bookingText(
            'errors.phoneInvalid',
            'Saisissez un numéro de téléphone valide.',
            'Enter a valid phone number.',
          ),
          PROPOSAL_BOOKING_REQUEST_ID_CONFLICT: this.bookingText(
            'errors.requestIdConflict',
            'La réservation a changé. Fermez puis rouvrez le calendrier.',
            'The booking has changed. Close and reopen the calendar.',
          ),
          PROPOSAL_BOOKING_RATE_LIMITED: this.bookingText(
            'errors.rateLimited',
            'Trop de demandes ont été envoyées. Réessayez un peu plus tard.',
            'Too many requests have been sent. Please try again later.',
          ),
          AUTOREPLY_RATE_LIMITED: this.bookingText(
            'errors.rateLimited',
            'Trop de demandes ont été envoyées. Réessayez un peu plus tard.',
            'Too many requests have been sent. Please try again later.',
          ),
          AUTOREPLY_REQUEST_IN_PROGRESS: this.bookingText(
            'errors.requestInProgress',
            'Cette réservation est déjà en cours de traitement.',
            'This booking is already being processed.',
          ),
          AUTOREPLY_REQUEST_ID_CONFLICT: this.bookingText(
            'errors.requestIdConflict',
            'La réservation a changé. Fermez puis rouvrez le calendrier.',
            'The booking has changed. Close and reopen the calendar.',
          ),
          AUTOREPLY_CAPTCHA_REQUIRED: this.bookingText(
            'errors.captchaRequired',
            'La vérification anti-spam doit être configurée pour confirmer.',
            'Anti-spam verification must be configured before you can confirm.',
          ),
          AUTOREPLY_CAPTCHA_REJECTED: this.bookingText(
            'errors.captchaRejected',
            'La vérification anti-spam a échoué. Réessayez.',
            'Anti-spam verification failed. Please try again.',
          ),
          AUTOREPLY_CAPTCHA_TIMEOUT: this.bookingText(
            'errors.captchaTimeout',
            'La vérification anti-spam a expiré. Réessayez.',
            'Anti-spam verification timed out. Please try again.',
          ),
          AUTOREPLY_CAPTCHA_UNAVAILABLE: this.bookingText(
            'errors.captchaUnavailable',
            'La vérification anti-spam est momentanément indisponible.',
            'Anti-spam verification is temporarily unavailable.',
          ),
          AUTOREPLY_SECURITY_UNAVAILABLE: this.bookingText(
            'errors.securityUnavailable',
            'La réservation est momentanément indisponible.',
            'Booking is temporarily unavailable.',
          ),
          PROPOSAL_BOOKING_DISABLED: this.bookingText(
            'errors.disabled',
            'La réservation en ligne n’est pas encore activée.',
            'Online booking has not been enabled yet.',
          ),
          PROPOSAL_BOOKING_NOT_CONFIGURED: this.bookingText(
            'errors.notConfigured',
            'La réservation en ligne n’est pas encore configurée.',
            'Online booking has not been configured yet.',
          )
        };
        return messages[String(error?.code || '')]
          || this.bookingText(
            'errors.generic',
            'Impossible de confirmer le rendez-vous pour le moment.',
            'Unable to confirm the appointment right now.',
          );
      }

      setStatus(message, isError = false) {
        if (!this.statusNode) return;
        this.statusNode.textContent = message;
        this.statusNode.classList.toggle('is-error', isError);
      }
    }
  );
}
if (!customElements.get('tmf-editorial-carousel')) {
  customElements.define(
    'tmf-editorial-carousel',
    class TmfEditorialCarousel extends HTMLElement {
      connectedCallback() {
        if (this._connected) return;

        this._connected = true;
        this.rail = this.querySelector('[data-feature-rail]');
        this.cards = Array.from(this.querySelectorAll('[data-feature-card]'));
        this.controls = this.querySelector('[data-feature-controls]');
        this.previousButton = this.querySelector('[data-feature-previous]');
        this.nextButton = this.querySelector('[data-feature-next]');
        this.status = this.querySelector('[data-feature-status]');
        this.toggles = Array.from(this.querySelectorAll('[data-feature-toggle]'));
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        if (!this.rail || !this.cards.length || !this.controls || !this.previousButton || !this.nextButton) {
          this.classList.add('is-visible');
          return;
        }

        this.toggles.forEach((toggle) => {
          toggle.hidden = false;
        });

        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        this.previousButton.addEventListener('click', () => this.moveBy(-1, true), { signal });
        this.nextButton.addEventListener('click', () => this.moveBy(1, true), { signal });
        this.toggles.forEach((toggle) => {
          toggle.addEventListener('click', () => this.toggleCardDetails(toggle), { signal });
        });
        this.rail.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
        this.rail.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
        this.rail.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
        this.rail.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal });
        this.rail.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
        this.rail.addEventListener('pointercancel', (event) => this.onPointerUp(event), { signal });
        document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });

        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.rail);
        this.cards.forEach((card) => this.resizeObserver.observe(card));

        this.prepareEntrance();
        requestAnimationFrame(() => this.updateControls());
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
        this.entranceObserver?.disconnect();
        cancelAnimationFrame(this.scrollFrame);
        this._connected = false;
      }

      prepareEntrance() {
        if (this.motionQuery.matches || !('IntersectionObserver' in window)) {
          this.classList.add('is-visible');
          return;
        }

        this.entranceObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.classList.add('is-visible');
            this.entranceObserver.disconnect();
          },
          { threshold: 0.08 }
        );
        this.entranceObserver.observe(this);
      }

      get isRtl() {
        return getComputedStyle(this.rail).direction === 'rtl';
      }

      get anchorPosition() {
        const railBounds = this.rail.getBoundingClientRect();
        const list = this.rail.querySelector('.tmf-feature-gallery__list');
        const listStyles = getComputedStyle(list);
        return this.isRtl
          ? railBounds.right - Number.parseFloat(listStyles.paddingRight)
          : railBounds.left + Number.parseFloat(listStyles.paddingLeft);
      }

      get currentIndex() {
        const anchor = this.anchorPosition;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.cards.forEach((card, index) => {
          const bounds = card.getBoundingClientRect();
          const cardEdge = this.isRtl ? bounds.right : bounds.left;
          const distance = Math.abs(cardEdge - anchor);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      scrollToIndex(index, announce = false) {
        const nextIndex = Math.max(0, Math.min(index, this.cards.length - 1));
        const cardBounds = this.cards[nextIndex].getBoundingClientRect();
        const cardEdge = this.isRtl ? cardBounds.right : cardBounds.left;
        const delta = cardEdge - this.anchorPosition;

        this.rail.scrollBy({
          left: delta,
          behavior: this.motionQuery.matches ? 'auto' : 'smooth',
        });

        if (announce) this.announce(nextIndex);
      }

      moveBy(step, announce = false) {
        this.scrollToIndex(this.currentIndex + step, announce);
      }

      onKeydown(event) {
        let destination;
        if (event.key === 'ArrowLeft') destination = this.currentIndex - 1;
        if (event.key === 'ArrowRight') destination = this.currentIndex + 1;
        if (event.key === 'Home') destination = 0;
        if (event.key === 'End') destination = this.cards.length - 1;
        if (destination === undefined) return;

        event.preventDefault();
        this.scrollToIndex(destination, true);
      }

      onScroll() {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.updateControls());
      }

      updateControls() {
        const hasOverflow = this.rail.scrollWidth > this.rail.clientWidth + 1;
        this.controls.hidden = !hasOverflow;

        if (!hasOverflow) {
          this.previousButton.disabled = true;
          this.nextButton.disabled = true;
          return;
        }

        const railBounds = this.rail.getBoundingClientRect();
        const list = this.rail.querySelector('.tmf-feature-gallery__list');
        const listStyles = getComputedStyle(list);
        const firstBounds = this.cards[0].getBoundingClientRect();
        const lastBounds = this.cards[this.cards.length - 1].getBoundingClientRect();
        const tolerance = 2;

        if (this.isRtl) {
          const startEdge = railBounds.right - Number.parseFloat(listStyles.paddingRight);
          const endEdge = railBounds.left + Number.parseFloat(listStyles.paddingLeft);
          this.previousButton.disabled = Math.abs(firstBounds.right - startEdge) <= tolerance;
          this.nextButton.disabled = Math.abs(lastBounds.left - endEdge) <= tolerance;
        } else {
          const startEdge = railBounds.left + Number.parseFloat(listStyles.paddingLeft);
          const endEdge = railBounds.right - Number.parseFloat(listStyles.paddingRight);
          this.previousButton.disabled = Math.abs(firstBounds.left - startEdge) <= tolerance;
          this.nextButton.disabled = Math.abs(lastBounds.right - endEdge) <= tolerance;
        }
      }

      announce(index) {
        if (!this.status) return;
        this.status.textContent = '';
        requestAnimationFrame(() => {
          this.status.textContent = `${index + 1} / ${this.cards.length}`;
        });
      }

      toggleCardDetails(toggle) {
        const card = toggle.closest('[data-feature-panel]');
        const details = card?.querySelector('[data-feature-details]');
        const visual = card?.querySelector('[data-feature-visual]');
        if (!card || !details || !visual) return;

        const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
        card.classList.toggle('is-details-visible', willOpen);
        toggle.setAttribute('aria-expanded', String(willOpen));
        details.setAttribute('aria-hidden', String(!willOpen));
        visual.setAttribute('aria-hidden', String(willOpen));

        const label = toggle.querySelector('[data-feature-toggle-label]');
        if (label) label.textContent = willOpen ? toggle.dataset.closeLabel : toggle.dataset.openLabel;
      }

      onPointerDown(event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target instanceof Element && event.target.closest('button, a, input, select, textarea')) return;
        this.dragState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          scrollLeft: this.rail.scrollLeft,
          moved: false,
        };
        this.rail.setPointerCapture(event.pointerId);
        this.rail.classList.add('is-dragging');
      }

      onPointerMove(event) {
        if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;
        const distance = event.clientX - this.dragState.startX;
        if (Math.abs(distance) > 4) this.dragState.moved = true;
        if (!this.dragState.moved) return;

        event.preventDefault();
        this.rail.scrollLeft = this.dragState.scrollLeft - distance;
      }

      onPointerUp(event) {
        if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;
        const shouldSnap = this.dragState.moved;
        this.dragState = null;
        this.rail.classList.remove('is-dragging');
        if (this.rail.hasPointerCapture(event.pointerId)) this.rail.releasePointerCapture(event.pointerId);
        if (shouldSnap) this.scrollToIndex(this.currentIndex, true);
      }

      onBlockSelect(event) {
        const selectedCard = this.querySelector(`[data-block-id="${CSS.escape(event.detail.blockId)}"]`);
        if (!selectedCard) return;
        const selectedIndex = this.cards.indexOf(selectedCard);
        if (selectedIndex >= 0) this.scrollToIndex(selectedIndex);
      }
    }
  );
}
